import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta, fechaHoraLocal, mxn } from '../formato';
import {
  DetalleOfertaCompra,
  detalleOfertaDe,
  esDeCompra,
  resumenDetalle,
} from './portal/CotizacionAduanal';
import type { Monto } from './tipos';

/** Estados de México (ISO 3166-2:MX sin prefijo), como los valida el dominio Proveedores. */
export const ESTADOS_MX: [string, string][] = [
  ['AGU', 'Aguascalientes'],
  ['BCN', 'Baja California'],
  ['BCS', 'Baja California Sur'],
  ['CAM', 'Campeche'],
  ['CHH', 'Chihuahua'],
  ['CHP', 'Chiapas'],
  ['CMX', 'Ciudad de México'],
  ['COA', 'Coahuila'],
  ['COL', 'Colima'],
  ['DUR', 'Durango'],
  ['GRO', 'Guerrero'],
  ['GUA', 'Guanajuato'],
  ['HID', 'Hidalgo'],
  ['JAL', 'Jalisco'],
  ['MEX', 'Estado de México'],
  ['MIC', 'Michoacán'],
  ['MOR', 'Morelos'],
  ['NAY', 'Nayarit'],
  ['NLE', 'Nuevo León'],
  ['OAX', 'Oaxaca'],
  ['PUE', 'Puebla'],
  ['QUE', 'Querétaro'],
  ['ROO', 'Quintana Roo'],
  ['SIN', 'Sinaloa'],
  ['SLP', 'San Luis Potosí'],
  ['SON', 'Sonora'],
  ['TAB', 'Tabasco'],
  ['TAM', 'Tamaulipas'],
  ['TLA', 'Tlaxcala'],
  ['VER', 'Veracruz'],
  ['YUC', 'Yucatán'],
  ['ZAC', 'Zacatecas'],
];

interface Necesidad {
  tipo: 'reparar' | 'reemplazar' | 'suministrar';
  descripcion: string;
  fotos: string[];
}
interface Convocatoria {
  id: string;
  folio: string;
  titulo: string;
  especialidad: string;
  vehiculo: { anio: number | null; marca: string | null; modelo: string | null };
  necesidades: Necesidad[];
  estadoMx: string;
  ciudad: string;
  entrega: string;
  mecanica: 'cerrada' | 'abierta';
  cierraEn: string;
  estado: 'abierta' | 'cerrada' | 'ganada' | 'no_ganada' | 'cancelada';
  mejorPrecioMxn: string | null;
  miOferta: { id: string; precioMxn: string; estado: string } | null;
  detalle?: Record<string, unknown> | null;
}
interface Especialidad {
  clave: string;
  nombre: string;
  grupo: string;
}

const ESTADO_CONVOCATORIA: Record<string, [string, string]> = {
  abierta: ['Abierta', 'aviso'],
  cerrada: ['Cerrada', ''],
  ganada: ['¡Ganaste!', 'exito'],
  no_ganada: ['No ganada', ''],
  cancelada: ['Cancelada', ''],
};
const TRABAJO: Record<string, string> = {
  reparar: 'Reparar',
  reemplazar: 'Reemplazar (pieza e instalación)',
  suministrar: 'Solo la pieza',
};
const ENTREGA: Record<string, string> = {
  en_lote: 'El auto está en el lote del cliente',
  recoleccion: 'Hay que recogerlo',
  envio: 'Se envía',
};
const vehiculo = (v: Convocatoria['vehiculo']) =>
  [v.anio, v.marca, v.modelo].filter(Boolean).join(' ') || 'Vehículo sin datos';

function Etiqueta({ estado }: { estado: string }) {
  const [texto, clase] = ESTADO_CONVOCATORIA[estado] ?? [estado, ''];
  return <span className={`etiqueta ${clase}`}>{texto}</span>;
}

/** Convocatorias de la especialidad y zona del proveedor (solo lo que el lotero publicó). */
export function Convocatorias() {
  const { datos, error } = useConsulta<Convocatoria[]>('/workshop/portal-proveedor/convocatorias');
  const { datos: perfil, estadoError } = useConsulta<{ verificacion: string }>(
    '/workshop/portal-proveedor/perfil',
  );
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Convocatorias</h1>
          <div className="subtitulo">
            Trabajos que piden los loteros de tu especialidad y tu zona
          </div>
        </div>
      </div>
      {estadoError === 404 && (
        <p className="error">
          Completa <Link to="/portal/perfil">tu perfil</Link> para entrar a la red de proveedores.
        </p>
      )}
      {perfil && perfil.verificacion !== 'verificado' && (
        <p
          className="etiqueta aviso"
          style={{ whiteSpace: 'normal', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
        >
          Tu perfil está {perfil.verificacion}: recibirás convocatorias de la red cuando la
          plataforma lo verifique.
        </p>
      )}
      {error && estadoError !== 404 && <p className="error">{error}</p>}
      <section className="seccion">
        {datos && !datos.length ? (
          <p className="vacio">Aún no tienes convocatorias.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Trabajo</th>
                <th>Dónde</th>
                <th>Cierra</th>
                <th className="num">Tu oferta</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {(datos ?? []).map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/portal/convocatorias/${c.id}`}>{c.titulo}</Link>
                    <div className="vin">
                      {vehiculo(c.vehiculo)} ·{' '}
                      {c.mecanica === 'abierta' ? 'subasta abierta' : 'sobre cerrado'}
                    </div>
                  </td>
                  <td>
                    {c.ciudad}, {c.estadoMx}
                  </td>
                  <td>{fechaCorta(c.cierraEn)}</td>
                  <td className="num">{c.miOferta ? mxn(c.miOferta.precioMxn) : '—'}</td>
                  <td>
                    <Etiqueta estado={c.estado} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

export function ConvocatoriaDetalle() {
  const { id } = useParams();
  const api = useApi();
  const { datos, error, recargar } = useConsulta<Convocatoria[]>(
    '/workshop/portal-proveedor/convocatorias',
  );
  const [aviso, setAviso] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const c = datos?.find((x) => x.id === id);
  const ofertar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setEnviando(true);
    setAviso(null);
    try {
      await api('PUT', `/workshop/portal-proveedor/convocatorias/${id}/oferta`, {
        precio: {
          cantidad: String(f.get('cantidad')),
          moneda: 'MXN',
          fecha: fechaHoraLocal(new Date()).slice(0, 10),
        },
        plazoDias: Number(f.get('plazoDias')),
        garantiaDias: Number(f.get('garantiaDias') || 0),
        incluyePieza: f.get('incluyePieza') === 'on',
        ...(f.get('tipoPieza') && { tipoPieza: f.get('tipoPieza') }),
        ...(f.get('notas') && { notas: f.get('notas') }),
        ...(c && esDeCompra(c.especialidad) && { detalle: detalleOfertaDe(c.especialidad, f) }),
      });
      setAviso('Oferta enviada. Puedes mejorarla mientras la convocatoria esté abierta.');
      recargar();
      // El mejor precio de la subasta abierta llega por eventos: se vuelve a consultar en unos segundos.
      setTimeout(recargar, 4000);
    } catch (err) {
      setAviso((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  const retirar = async () => {
    try {
      await api('DELETE', `/workshop/portal-proveedor/convocatorias/${id}/oferta`);
      setAviso('Retiraste tu oferta.');
      recargar();
      setTimeout(recargar, 4000);
    } catch (err) {
      setAviso((err as Error).message);
    }
  };
  if (error) return <p className="error">{error}</p>;
  if (!datos) return <p className="subtitulo">Cargando…</p>;
  if (!c) return <p className="error">Convocatoria no encontrada.</p>;
  const abierta = c.estado === 'abierta';
  // Mientras llega el evento, la oferta vigente propia ya cuenta como mejor precio si es menor.
  const propia = c.miOferta?.estado === 'vigente' ? c.miOferta.precioMxn : null;
  const mejorPrecio =
    propia && (!c.mejorPrecioMxn || Number(propia) < Number(c.mejorPrecioMxn))
      ? propia
      : c.mejorPrecioMxn;
  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/portal/convocatorias">Convocatorias</Link> › {c.folio}
          </div>
          <h1>{c.titulo}</h1>
          <div className="subtitulo">
            {vehiculo(c.vehiculo)} · {c.ciudad}, {c.estadoMx} · cierra {fechaCorta(c.cierraEn)}
          </div>
        </div>
        <div className="relleno" />
        <Etiqueta estado={c.estado} />
      </div>
      <section className="seccion">
        <header>
          <h2>Qué se necesita</h2>
        </header>
        <div className="cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {c.necesidades.map((n, i) => (
            <div key={i}>
              <strong>{TRABAJO[n.tipo]}:</strong> {n.descripcion}
            </div>
          ))}
          <div className="subtitulo">{ENTREGA[c.entrega] ?? c.entrega}</div>
          {c.detalle && esDeCompra(c.especialidad) && (
            <div className="subtitulo">{resumenDetalle(c.especialidad, c.detalle)}</div>
          )}
          <div className="subtitulo">
            {c.mecanica === 'abierta'
              ? mejorPrecio
                ? `Subasta abierta: el mejor precio vigente es ${mxn(mejorPrecio)} (no se sabe de quién).`
                : 'Subasta abierta: todavía nadie ha ofertado.'
              : 'Sobre cerrado: nadie ve las ofertas de los demás.'}
          </div>
        </div>
      </section>
      <section className="seccion">
        <header>
          <h2>{c.miOferta ? 'Tu oferta' : 'Ofertar'}</h2>
          {c.miOferta && (
            <span className="subtitulo">
              {mxn(c.miOferta.precioMxn)} · {c.miOferta.estado.replace('_', ' ')}
            </span>
          )}
        </header>
        {abierta ? (
          <form className="cuerpo formulario" onSubmit={(e) => void ofertar(e)}>
            <label className="campo">
              <span>Precio total (MXN, con IVA si lo cobras)</span>
              <input name="cantidad" required inputMode="decimal" pattern="\d{1,12}(\.\d{1,2})?" />
            </label>
            <label className="campo">
              <span>Plazo (días)</span>
              <input name="plazoDias" type="number" min={0} max={365} required defaultValue={3} />
            </label>
            <label className="campo">
              <span>Garantía (días)</span>
              <input name="garantiaDias" type="number" min={0} max={3650} defaultValue={90} />
            </label>
            <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
              <input name="incluyePieza" type="checkbox" style={{ minHeight: 0 }} />
              <span>Incluye la pieza</span>
            </label>
            <label className="campo">
              <span>Tipo de pieza</span>
              <select name="tipoPieza" defaultValue="">
                <option value="">—</option>
                <option value="original">Original</option>
                <option value="nueva">Nueva genérica</option>
                <option value="generica">Genérica</option>
                <option value="usada">Usada</option>
              </select>
            </label>
            {esDeCompra(c.especialidad) && <DetalleOfertaCompra especialidad={c.especialidad} />}
            <label className="campo" style={{ gridColumn: '1 / -1' }}>
              <span>Notas para el cliente</span>
              <textarea name="notas" rows={2} maxLength={2000} />
            </label>
            {aviso && (
              <p className="subtitulo" style={{ gridColumn: '1 / -1' }}>
                {aviso}
              </p>
            )}
            <div className="acciones">
              {c.miOferta?.estado === 'vigente' && (
                <button type="button" className="boton" onClick={() => void retirar()}>
                  Retirar oferta
                </button>
              )}
              <button className="boton primario" disabled={enviando}>
                {c.miOferta ? 'Mejorar oferta' : 'Enviar oferta'}
              </button>
            </div>
          </form>
        ) : (
          <p className="vacio">La convocatoria ya no recibe ofertas.</p>
        )}
      </section>
    </>
  );
}

interface TrabajoAsignado {
  id: string;
  folio: string;
  titulo: string;
  precio: Monto;
  estado: string;
  estadoPago: string;
  pagadoMxn: string;
  calificacion: { precio: number; plazo: number; calidad: number } | null;
  adjudicadoEn: string;
  aceptadoEn: string | null;
}

/** Trabajos que el proveedor ganó, con su avance y su cobro. */
export function MisTrabajos() {
  const { datos, error } = useConsulta<TrabajoAsignado[]>('/workshop/portal-proveedor/trabajos');
  return (
    <>
      <h1>Mis trabajos</h1>
      {error && <p className="error">{error}</p>}
      <section className="seccion">
        {datos && !datos.length ? (
          <p className="vacio">Cuando ganes una convocatoria, tu trabajo aparece aquí.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Trabajo</th>
                <th className="num">Precio</th>
                <th className="num">Cobrado</th>
                <th>Estado</th>
                <th>Calificación</th>
              </tr>
            </thead>
            <tbody>
              {(datos ?? []).map((t) => (
                <tr key={t.id}>
                  <td>
                    {t.titulo}
                    <div className="vin">
                      {t.folio} · adjudicado {fechaCorta(t.adjudicadoEn)}
                    </div>
                  </td>
                  <td className="num">{mxn(t.precio.equivalenteMxn)}</td>
                  <td className="num">{mxn(t.pagadoMxn)}</td>
                  <td>
                    <span className={`etiqueta ${t.estado === 'aceptado' ? 'exito' : ''}`}>
                      {t.estado.replace('_', ' ')}
                    </span>{' '}
                    <span
                      className={`etiqueta ${t.estadoPago === 'pagado' ? 'exito' : t.estadoPago === 'disputa' ? 'peligro' : 'aviso'}`}
                    >
                      pago {t.estadoPago}
                    </span>
                  </td>
                  <td>
                    {t.calificacion
                      ? `${(Math.round(((t.calificacion.precio + t.calificacion.plazo + t.calificacion.calidad) / 3) * 10) / 10).toFixed(1)} ★`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

interface PagoRecibido {
  id: string;
  monto: Monto;
  metodo: string;
  referencia: string | null;
  fecha: string;
}

export function Cobros() {
  const { datos, error } = useConsulta<PagoRecibido[]>('/workshop/portal-proveedor/pagos');
  const total = (datos ?? []).reduce((t, p) => t + Number(p.monto.equivalenteMxn), 0);
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Cobros</h1>
          <div className="subtitulo">Pagos que los loteros registraron a tu favor</div>
        </div>
        <div className="relleno" />
        {datos && datos.length > 0 && (
          <div className="tarjeta">Total cobrado: {mxn(total.toFixed(2))}</div>
        )}
      </div>
      {error && <p className="error">{error}</p>}
      <section className="seccion">
        {datos && !datos.length ? (
          <p className="vacio">Sin cobros registrados.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Método</th>
                <th>Referencia</th>
                <th className="num">Monto</th>
              </tr>
            </thead>
            <tbody>
              {(datos ?? []).map((p) => (
                <tr key={p.id}>
                  <td>{fechaCorta(p.fecha)}</td>
                  <td>{p.metodo}</td>
                  <td>{p.referencia ?? '—'}</td>
                  <td className="num">{mxn(p.monto.equivalenteMxn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

interface Perfil {
  nombreComercial: string;
  descripcion: string | null;
  telefono: string | null;
  correo: string | null;
  ciudad: string;
  estadoMx: string;
  especialidades: string[];
  estadosCobertura: string[];
  verificacion: string;
  notaVerificacion: string | null;
  calificacion: number | null;
  trabajosTerminados: number;
}

/** Perfil del proveedor: lo publicado en la red (sin contacto) y su contacto privado. */
export function MiPerfil() {
  const api = useApi();
  const perfil = useConsulta<Perfil>('/workshop/portal-proveedor/perfil');
  const { datos: especialidades } = useConsulta<Especialidad[]>('/workshop/especialidades');
  const [aviso, setAviso] = useState<string | null>(null);
  const p = perfil.datos;
  const guardar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const texto = (k: string) => String(f.get(k) ?? '').trim();
    setAviso(null);
    try {
      await api('PUT', '/workshop/portal-proveedor/perfil', {
        nombreComercial: texto('nombreComercial'),
        ...(texto('descripcion') && { descripcion: texto('descripcion') }),
        ...(texto('telefono') && { telefono: texto('telefono') }),
        ...(texto('correo') && { correo: texto('correo') }),
        ciudad: texto('ciudad'),
        estadoMx: texto('estadoMx'),
        especialidades: f.getAll('especialidades').map(String),
        estadosCobertura: f.getAll('estadosCobertura').map(String),
      });
      setAviso('Perfil guardado.');
      perfil.recargar();
    } catch (err) {
      setAviso((err as Error).message);
    }
  };
  if (perfil.cargando) return <p className="subtitulo">Cargando…</p>;
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Mi perfil de proveedor</h1>
          <div className="subtitulo">
            Los loteros ven tu nombre, tus especialidades, tu zona y tu reputación; tu contacto no
            se publica en la red
          </div>
        </div>
        <div className="relleno" />
        {p && (
          <span className={`etiqueta ${p.verificacion === 'verificado' ? 'exito' : 'aviso'}`}>
            {p.verificacion === 'verificado' ? 'Verificado' : `Verificación ${p.verificacion}`}
          </span>
        )}
      </div>
      {p?.notaVerificacion && (
        <p className="subtitulo">Nota de la plataforma: {p.notaVerificacion}</p>
      )}
      <section className="seccion">
        <form className="cuerpo formulario" onSubmit={(e) => void guardar(e)}>
          <label className="campo">
            <span>Nombre comercial</span>
            <input
              name="nombreComercial"
              required
              maxLength={120}
              defaultValue={p?.nombreComercial ?? ''}
            />
          </label>
          <label className="campo">
            <span>Teléfono</span>
            <input name="telefono" type="tel" maxLength={30} defaultValue={p?.telefono ?? ''} />
          </label>
          <label className="campo">
            <span>Correo</span>
            <input name="correo" type="email" defaultValue={p?.correo ?? ''} />
          </label>
          <label className="campo">
            <span>Ciudad</span>
            <input name="ciudad" required maxLength={80} defaultValue={p?.ciudad ?? ''} />
          </label>
          <label className="campo">
            <span>Estado</span>
            <select name="estadoMx" defaultValue={p?.estadoMx ?? 'JAL'}>
              {ESTADOS_MX.map(([clave, nombre]) => (
                <option key={clave} value={clave}>
                  {nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="campo" style={{ gridColumn: '1 / -1' }}>
            <span>Descripción</span>
            <textarea
              name="descripcion"
              rows={2}
              maxLength={2000}
              defaultValue={p?.descripcion ?? ''}
            />
          </label>
          <fieldset className="campo" style={{ gridColumn: '1 / -1', border: 0, padding: 0 }}>
            <span>Especialidades</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
              {(especialidades ?? []).map((e) => (
                <label key={e.clave} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    name="especialidades"
                    value={e.clave}
                    defaultChecked={p?.especialidades.includes(e.clave)}
                  />
                  {e.nombre}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset className="campo" style={{ gridColumn: '1 / -1', border: 0, padding: 0 }}>
            <span>Estados donde das servicio</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
              {ESTADOS_MX.map(([clave, nombre]) => (
                <label key={clave} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    name="estadosCobertura"
                    value={clave}
                    defaultChecked={p?.estadosCobertura.includes(clave)}
                  />
                  {nombre}
                </label>
              ))}
            </div>
          </fieldset>
          {aviso && (
            <p className="subtitulo" style={{ gridColumn: '1 / -1' }}>
              {aviso}
            </p>
          )}
          <div className="acciones">
            <button className="boton primario">Guardar perfil</button>
          </div>
        </form>
      </section>
    </>
  );
}
